import { Song } from "@/lib/music/song";
import { BaseControler } from "./base-controler";
import { SalamanderPianoSynth, type Synth } from "@/lib/audio/synth";
import { Lane } from "../lane";
import { ScheduleTicker } from "@/lib/app/schedule-ticker";
import { Transport } from "../transport";
import { LaneOrigin } from "./controler";

function deriveHandFromRole(role: string | null): 'left' | 'right' | null {
  if (role === 'left-hand') {
    return 'left';
  }

  if (role === 'right-hand') {
    return 'right';
  }

  return null;
}

export class SongControler extends BaseControler {
  private clock?: ScheduleTicker;
  private lanes = new Set<Lane>();
  private laneOrigins = new Map<Lane, LaneOrigin>();

  /**
   * Le contexte audio est passé, et non tiré du transport.
   *
   * Le transport ne connaît plus que l'horloge de position : la sortie audio
   * appartient à l'appareil, et il n'y a plus de chemin — ni de raison d'en
   * ouvrir un — qui aille de l'un à l'autre.
   */
  constructor(
    private song: Song,
    private transport: Transport,
    private readonly context: AudioContext,
  ) {
    super();
  }

  async load(): Promise<void> {
    const ctx = this.context;
    const timeline = this.song.getMusicalTimeline();
    this.transport.setMusicalTimeline(timeline);

    /*
      Un synthé par lane, et non un par instrument.

      Le partage venait du coût de chargement : deux `SalamanderPianoSynth`
      pour la main gauche et la main droite du même piano, c'étaient trente MP3
      téléchargés et surtout **décodés** deux fois. Le cache d'échantillons
      décodés (`sample-buffer-cache`) a supprimé ce coût, et le partage n'avait
      plus que ses défauts.

      Car `SalamanderPianoSynth.activeNotes` est indexé par numéro MIDI seul, et
      un `noteOn` relâche « l'instance précédente de cette note ». Deux lanes
      sur un même synthé, c'est donc un do de la main gauche coupé net dès que
      la droite en joue un — le cas de toutes les pièces où les mains se
      croisent, ou se doublent à l'octave.
    */
    const synths: Synth[] = [];

    for (const part of this.song.getParts()) {
      const instrument = part.getInstrument();
      const synth = new SalamanderPianoSynth(ctx);
      synths.push(synth);

      const lane = new Lane(
        0, // channel par défaut
        synth,
        instrument,
      );

      // Add all notes (exhaustive, for piano-roll rendering + audio scheduling)
      for (const n of part.getRawNotes()) {
        lane.addNote(n.note, n.tick, n.durationTicks, n.velocity, n.finger);
      }

      // Add clusters separately (chords, roman numerals, voicing)
      for (const { value: c } of part.getClusters().entries()) {
        lane.addCluster(c);
      }

      /*
        Le pédalage du morceau, dans chaque lane.

        Un piano n'a qu'un jeu d'étouffoirs, mais chaque lane a son synthé et
        son ordonnanceur — et c'est l'ordonnanceur qui retient les
        relâchements. Écrite sur la seule main droite, la pédale laisserait la
        gauche s'éteindre au lever du doigt, c'est-à-dire précisément ce
        qu'elle est là pour empêcher.

        Après les notes, et pas avant : deux événements au même tick sonnent
        dans l'ordre où ils sont entrés, et c'est l'ordre juste. Une note qui
        s'achève à l'instant où la pédale s'enfonce n'est pas rattrapée —
        l'étouffoir tombe avant que la pédale ne le relève —, tandis qu'une
        note qui commence à l'instant où la pédale se lève n'est pas coupée.
      */
      for (const { tick, down } of this.song.getSustain()) {
        lane.addSustain(tick, down);
      }

      const partRole = part.getRole();
      this.laneOrigins.set(lane, {
        kind: 'song-part',
        partRole,
        hand: deriveHandFromRole(partRole),
      });

      this.lanes.add(lane);
    }

    // Wait for every synth to have its samples loaded before resolving.
    // Without this, `noteOn()` calls scheduled right after `play()` are
    // silently dropped (see `SalamanderPianoSynth.isReady` early-return)
    //, the typical symptom is "only the last 1-2 notes of a short
    // sequence are audible". Long pieces hide it because count-in /
    // playback latency masks the gap.
    await Promise.all(synths.map((s) => s.whenReady()));
  }

  start(clock: ScheduleTicker): void {
    this.clock = clock;

    // Émet toutes les lanes créées pendant load()
    this.lanes.forEach(lane => {
      this.emit('lane:created', lane);
    });
  }

  getSong(): Song {
    return this.song;
  }

  /**
   * Les lanes du morceau, dans l'ordre de ses parties, dès que `load()` a
   * rendu la main — et sans attendre qu'une piste les ait demandées par
   * `start()`.
   *
   * C'est ce qui permet à un morceau d'exister sans piste : une séquence
   * garde ses morceaux hors scène chargés, les dessine sur le rouleau à leur
   * place, et ne les monte en piste — donc ne les fait sonner — que quand la
   * tête de lecture arrive dessus. Voir `SongSequence`.
   */
  getLanes(): readonly Lane[] {
    return Array.from(this.lanes);
  }

  /**
   * Mute/unmute every lane owned by this controler.
   *
   * Scoped to the song's lanes only, does not affect the player's
   * piano lane (PianoControler) nor the metronome, since those are
   * owned by other controlers/components.
   *
   * Used by the learning journey to silence the song during S4
   * (rhythm phase) so the player drives the audio.
   */
  setMuted(muted: boolean): void {
    this.lanes.forEach((lane) => {
      if (muted) {
        lane.mute();

        return;
      }

      lane.unmute();
    });
  }

  /**
   * Sound only the lanes of `hand`, and mute every other lane.
   *
   * The learning journey's accompanied pass: the application takes one hand
   * in time while the player takes the other. Done here rather than by
   * scheduling notes on the player's piano because this rides the transport
   * itself — same clock, same playback rate, sample-accurate against the
   * metronome. A `setTimeout` accompaniment drifts against the click, which
   * is the one thing the step is teaching.
   *
   * The song is split into parts by the analyzer's per-note hand assignment
   * (`song-factory.ts`), so a hand lane exists whenever the analysis carries
   * hands. `hasHandLanes()` says whether it does; a song without them can
   * only be muted whole.
   */
  soloHand(hand: 'left' | 'right'): void {
    this.lanes.forEach((lane) => {
      const origin = this.laneOrigins.get(lane);

      if (origin?.kind === 'song-part' && origin.hand === hand) {
        lane.unmute();

        return;
      }

      lane.mute();
    });
  }

  /** True when the song's lanes carry hands, so `soloHand` can do anything. */
  hasHandLanes(): boolean {
    let left = false;
    let right = false;

    this.lanes.forEach((lane) => {
      const origin = this.laneOrigins.get(lane);
      const hand = origin?.kind === 'song-part' ? origin.hand : null;

      if (hand === 'left') {
        left = true;
      }

      if (hand === 'right') {
        right = true;
      }
    });

    return left && right;
  }

  getPartCount(): number {
    return this.song.getParts().length;
  }

  getPartNames(): string[] {
    return this.song.getParts().map((p, i) => p.getName() || `Part ${i + 1}`);
  }

  getDurationSeconds(): number {
    const timeline = this.song.getMusicalTimeline();

    return timeline.tickToSeconds(this.song.getDurationTicks());
  }

  getLaneOrigin(lane: Lane): LaneOrigin | undefined {
    return this.laneOrigins.get(lane);
  }

  destroy(): void {
    this.lanes.forEach(lane => this.emit('lane:removed', lane));
    this.lanes.clear();
    this.laneOrigins.clear();
    super.destroy();
  }
}
